import type {EquationLine, ScatterAxis, ScatterDataPoint, SvgPathOverlay} from '../../utils/pebbleplotScatterSchema';
import {generatePointsFromEquation, type Point2D} from '../../utils/evaluateExpression';

export interface ScatterOverlayContext {
  xAxis?: ScatterAxis;
  yAxis?: ScatterAxis;
}

export interface ScatterOverlayResolved {
  equationPoints: Point2D[];
  svgPaths: SvgPathOverlay[];
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function collectOverlayPoints(params: {
  data?: ScatterDataPoint[];
  equationLines?: EquationLine[];
  svgPaths?: SvgPathOverlay[];
  context: ScatterOverlayContext;
}): ScatterOverlayResolved {
  const equationLines = params.equationLines ?? [];
  const svgPaths = params.svgPaths ?? [];

  const axisMin = params.context.xAxis?.min;
  const axisMax = params.context.xAxis?.max;
  const fallbackRange = isFiniteNumber(axisMin) && isFiniteNumber(axisMax) ? {min: axisMin, max: axisMax} : undefined;

  const equationPoints = equationLines.flatMap((line) => {
    const equationType = line.equationType ?? 'function';

    if (equationType === 'circle') {
      if (!isFiniteNumber(line.centerX) || !isFiniteNumber(line.centerY) || !isFiniteNumber(line.radius)) {
        return [];
      }

      return generatePointsFromEquation({
        equation: '',
        equationType: 'circle',
        range: {min: 0, max: 1},
        circle: {centerX: line.centerX, centerY: line.centerY, radius: line.radius},
      });
    }

    const equation = line.equation ?? '';
    const range = line.range ?? fallbackRange;
    if (!range) return [];

    return generatePointsFromEquation({
      equation,
      equationType,
      range,
    });
  });

  return {equationPoints, svgPaths};
}

export function buildSvgPathDFromPoints(
  points: Point2D[],
  scaleX: (v: number) => number,
  scaleY: (v: number) => number,
): string {
  const segments = points.reduce<string[]>((acc, p) => {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return acc;
    const x = scaleX(p.x);
    const y = scaleY(p.y);
    const cmd = acc.length === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    return acc.concat(cmd);
  }, []);

  return segments.join(' ');
}

export function getFirstMoveToPoint(svgPath: string): {x: number; y: number} | null {
  // Parse a simple "M x y" start (absolute). Since we only need a label anchor, keep it minimal.
  const match = svgPath.match(/M\s*([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)/i);
  if (!match) return null;
  const x = Number(match[1]);
  const y = Number(match[2]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return {x, y};
}
